import { useState, useEffect } from 'react';
import { Bookmark, BookmarkCheck } from 'lucide-react';
import { AuctionWithItems } from '../types';
import { apiService } from '../services/api';
import { useAuthStore } from '../services/auth';

interface SaveAuctionButtonProps {
  auction: AuctionWithItems;
  className?: string;
  showText?: boolean;
}

export default function SaveAuctionButton({ 
  auction, 
  className = '', 
  showText = true 
}: SaveAuctionButtonProps) {
  const { user } = useAuthStore();
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Check if auction is already saved
  useEffect(() => {
    const checkSavedStatus = async () => {
      if (!user || user.id === auction.ownerId) return;
      
      try {
        const result = await apiService.checkAuctionSaved(auction.id);
        if (result.success) {
          setIsSaved(result.data.isSaved);
        }
      } catch (err) {
        console.error('Failed to check auction saved status:', err);
      }
    };

    checkSavedStatus();
  }, [user, auction.id, auction.ownerId]);

  const handleToggleSave = async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      setError('');
      
      if (isSaved) {
        // Remove from saved auctions
        const result = await apiService.removeSavedAuction(auction.id);
        if (result.success) {
          setIsSaved(false);
        } else {
          setError('Failed to remove auction');
        }
      } else {
        // Save auction
        const result = await apiService.saveAuction(auction.id);
        if (result.success) {
          setIsSaved(true);
        } else {
          setError('Failed to save auction');
        }
      }
    } catch (err) {
      setError(isSaved ? 'Failed to remove auction' : 'Failed to save auction');
    } finally {
      setIsLoading(false);
    }
  };

  // Don't show for unauthenticated users or auction owners
  if (!user || user.id === auction.ownerId) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={handleToggleSave}
        disabled={isLoading}
        className={`group relative p-2 rounded-md border-2 transition-all duration-200 hover:shadow-md min-h-[44px] min-w-[44px] touch-manipulation ${
          isSaved
            ? 'border-green-300 hover:border-green-400 bg-green-50 text-green-700 dark:border-green-600 dark:hover:border-green-500 dark:bg-green-900/20 dark:text-green-400'
            : 'border-gray-300 hover:border-gray-400 bg-white text-gray-600 dark:border-dark-secondary dark:hover:border-dark-secondary dark:bg-dark-tertiary dark:text-gray-300'
        } disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        title={isSaved ? 'Remove from saved auctions' : 'Save auction to your account'}
      >
        {isLoading ? (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current mx-auto"></div>
        ) : isSaved ? (
          <BookmarkCheck className="h-5 w-5 mx-auto" />
        ) : (
          <Bookmark className="h-5 w-5 mx-auto" />
        )}
        {showText && (
          <span className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 text-xs font-medium whitespace-nowrap bg-gray-900 text-white px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
            {isLoading ? 'Saving...' : isSaved ? 'Saved' : 'Save'}
          </span>
        )}
      </button>
      
      {error && (
        <div className="absolute top-full left-0 mt-1 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-400 whitespace-nowrap z-10">
          {error}
        </div>
      )}
    </div>
  );
}
